import { memo } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import type { Inventory } from "@/api/inventory/_models/Inventory";
import { OverflowTooltip } from "@/components/OverflowTooltip";
import { InventoryListExpanded } from "./inventory-list-expanded/InventoryListExpanded";
import { InventoryListStatusChip } from "./InventoryListStatusChip";
import { InventoryListTemplate } from "./InventoryListTemplate";
import { INVENTORY_LIST_ROW_CONTENT_PX, INVENTORY_LIST_SYSTEM_LABEL, toCin7Level, toOnHandDifference, toThreePlLevelList } from "./inventoryList.utils";

interface InventoryListRowProps {
  isOpen: boolean;
  onToggle: (inventoryId: string) => void;
  row: Inventory;
}

const OPEN_MARKER_PX = 3;

const toQuantityText = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  return value.toLocaleString();
};

/** The 3PL-less-Cin7 difference as signed text, with its colour and weight. */
const toDifferenceDisplay = (difference: number | null, row: Inventory): { text: string; colour: string; isBold: boolean } => {
  if (difference === null) return { text: "—", colour: "text.disabled", isBold: false };
  if (difference === 0) return { text: "0", colour: "text.disabled", isBold: false };
  let colour = "warning.main";
  if (row.status === "missingInCin7") colour = "error.main";
  let text = `−${Math.abs(difference).toLocaleString()}`;
  if (difference > 0) text = `+${difference.toLocaleString()}`;
  return { text, colour, isBold: true };
};

const InventoryListRowContent = (props: InventoryListRowProps) => {
  const { isOpen, onToggle, row } = props;
  const cin7 = toCin7Level(row);
  const threePl = toThreePlLevelList(row);
  const difference = toOnHandDifference(row);
  const productName = row.productName ?? "—";
  const differenceDisplay = toDifferenceDisplay(difference, row);

  const handleToggle = () => {
    onToggle(row.id);
  };

  return (
    <Box
      sx={{
        bgcolor: isOpen ? "action.hover" : "transparent",
        borderBottom: 1,
        borderColor: "divider",
        boxShadow: (theme) => (isOpen ? `inset ${OPEN_MARKER_PX}px 0 0 ${(theme.vars ?? theme).palette.primary.main}` : "none"),
      }}
    >
      <InventoryListTemplate
        onClick={handleToggle}
        sx={{
          cursor: "pointer",
          minHeight: INVENTORY_LIST_ROW_CONTENT_PX,
          py: 0.5,
          "&:hover": { bgcolor: isOpen ? "transparent" : "action.hover" },
        }}
      >
        <IconButton
          aria-label={isOpen ? "Collapse levels" : "Expand levels"}
          size="small"
          sx={{ gridArea: "expand" }}
        >
          <KeyboardArrowDownIcon
            fontSize="small"
            sx={{
              transform: isOpen ? "rotate(180deg)" : "none",
              transition: "transform 160ms",
            }}
          />
        </IconButton>
        <OverflowTooltip title={row.sku}>
          <Typography
            noWrap
            sx={{
              fontWeight: 600,
              gridArea: "sku",
            }}
            variant="body2"
          >
            {row.sku}
          </Typography>
        </OverflowTooltip>
        <OverflowTooltip title={productName}>
          <Typography
            noWrap
            sx={{ gridArea: "product" }}
            variant="body2"
          >
            {productName}
          </Typography>
        </OverflowTooltip>
        <OverflowTooltip title={row.locationName}>
          <Typography
            color="text.secondary"
            noWrap
            sx={{ gridArea: "location" }}
            variant="body2"
          >
            {row.locationName}
          </Typography>
        </OverflowTooltip>
        <Box sx={{ gridArea: "status" }}>
          <InventoryListStatusChip status={row.status} />
        </Box>
        <Typography
          sx={{
            fontVariantNumeric: "tabular-nums",
            gridArea: "cin7",
            textAlign: "right",
          }}
          variant="body2"
        >
          {toQuantityText(cin7?.onHand)}
        </Typography>
        <Box
          sx={{
            gridArea: "threePl",
            textAlign: "right",
          }}
        >
          {threePl.length === 0 && (
            <Typography
              color="text.secondary"
              sx={{ pr: "52px" }}
              variant="body2"
            >
              —
            </Typography>
          )}
          {threePl.map((level) => (
            <Typography
              key={level.system}
              noWrap
              sx={{
                columnGap: 1,
                display: "flex",
                justifyContent: "flex-end",
              }}
              variant="body2"
            >
              <Box
                component="span"
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {toQuantityText(level.onHand)}
              </Box>
              <Typography
                color="text.disabled"
                component="span"
                sx={{
                  fontWeight: 600,
                  textAlign: "left",
                  width: 44,
                }}
                variant="caption"
              >
                {INVENTORY_LIST_SYSTEM_LABEL[level.system] ?? level.system}
              </Typography>
            </Typography>
          ))}
        </Box>
        <Typography
          sx={{
            color: differenceDisplay.colour,
            fontVariantNumeric: "tabular-nums",
            fontWeight: differenceDisplay.isBold ? 600 : 400,
            gridArea: "difference",
            textAlign: "right",
          }}
          variant="body2"
        >
          {differenceDisplay.text}
        </Typography>
      </InventoryListTemplate>
      <Collapse
        in={isOpen}
        timeout={150}
        unmountOnExit
      >
        <InventoryListExpanded row={row} />
      </Collapse>
    </Box>
  );
};

/** One product at one location: Cin7's on-hand, the 3PL's, the difference and the status; clicking it expands every system's levels inline. Memoised. */
export const InventoryListRow = memo(InventoryListRowContent);
